import React from 'react';
import {
  Paper, Box, Typography, Divider, FormControl,
  InputLabel, Select, MenuItem, TextField
} from '@mui/material';
import ActionButtons from '../../../commun/ActionButtons';
import EditableField from './EditableField';
import './detailTalent.css';
import {
  disponibiliteOptions, statusOptions, statusOptionsRecruteur
} from '../../../commun/constants/options';
import ChipInput from '../../../commun/ChipInput';
import PhoneChipInput from '../../../commun/PhoneChipInput';

const displayOrUnknown = (val) => {
  if (!val || val === 'null') return 'Non renseigné';
  if (Array.isArray(val)) return val.length ? val.join(', ') : 'Non renseigné';
  return val.toString().trim() !== '' ? val : 'Non renseigné';
};

const MissionInfo = ({
  editableTalent,
  originalTalent,
  setEditableTalent,
  onSave,
  onFieldChange,
  formData,
  isEditing,
  setIsEditing
}) => {
  const userRole = localStorage.getItem("userType");
  const otherstatusOptions =
    (userRole === 'admin' || userRole === 'commercial' || userRole === 'agent')
      ? statusOptions
      : statusOptionsRecruteur;

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => {
    setEditableTalent(originalTalent);
    setIsEditing(false);
  };
  const handleSave = async () => {
    await onSave();
    setIsEditing(false);
  };

  return (
    <Paper elevation={3} className="mission-card-modern">
      <Box className="mission-header" display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" className="mission-title">Identité du talent</Typography>
        <ActionButtons
          isEditing={isEditing}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onSave={handleSave}
          showView={false}
          showDelete={false}
          showDownload={false}
        />
      </Box>

      <Box mb={2}>
        <Typography className="label-text" >Profil du talent</Typography>
        {isEditing ? (
          <TextField
            fullWidth
            rows={4}
            value={Array.isArray(editableTalent.pointsEssentiels)
              ? editableTalent.pointsEssentiels.join('\n')
              : editableTalent.pointsEssentiels || ''}
            onChange={(e) =>
              onFieldChange('pointsEssentiels', e.target.value.split('\n').filter(l => l.trim() !== ''))
            }
          />
        ) : (
          <Typography>{displayOrUnknown(editableTalent?.pointsEssentiels)}</Typography>
        )}
      </Box>

      <Divider className="mission-divider" />

      {/* Nom / Prénom */}
      <Box display="flex" gap={2} mb={2}>
        <Box flex={1}>
          <EditableField label="Nom" field="nomCandidat" isEditing={isEditing} value={editableTalent?.nomCandidat} onChange={onFieldChange} />
        </Box>
        <Box flex={1}>
          <EditableField label="Prénom" field="prenomCandidat" isEditing={isEditing} value={editableTalent?.prenomCandidat} onChange={onFieldChange} />
        </Box>
      </Box>

      {/* Email / Téléphone */}
      <Box display="flex" gap={2} mb={2}>
        <Box flex={1}>
          <Typography variant="subtitle2" className="label-text">Email</Typography>
          {isEditing ? (
            <ChipInput values={Array.isArray(editableTalent.email) ? editableTalent.email : [editableTalent.email].filter(Boolean)} onChange={(newEmails) => onFieldChange('email', newEmails)} fullWidth />
          ) : (
            <Typography>{displayOrUnknown(editableTalent?.email)}</Typography>
          )}
        </Box>
        <Box flex={1}>
          <Typography variant="subtitle2" className="label-text">Téléphone</Typography>
          {isEditing ? (
            <PhoneChipInput values={Array.isArray(editableTalent.phone) ? editableTalent.phone : []} onChange={(newPhones) => onFieldChange('phone', newPhones)} fullWidth />
          ) : (
            <Typography>{displayOrUnknown(editableTalent?.phone)}</Typography>
          )}
        </Box>
      </Box>

      {/* LinkedIn / Autre lien */}
      <Box display="flex" gap={2} mb={2}>
        <Box flex={1}>
          <EditableField label="LinkedIn" field="LinkedIn" isEditing={isEditing} value={editableTalent?.LinkedIn} onChange={onFieldChange} />
        </Box>
        <Box flex={1}>
          <EditableField label="Autre lien" field="Autrelien" isEditing={isEditing} value={editableTalent?.Autrelien} onChange={onFieldChange} />
        </Box>
      </Box>

      {/* En poste ? */}
      <Box mb={2}>
        <Typography variant="subtitle2" className="label-text">Ce talent est-il actuellement en poste ?</Typography>
        {isEditing ? (
          <FormControl fullWidth>
            <Select
              value={editableTalent?.poste || ''}
              onChange={(e) => onFieldChange('poste', e.target.value)}
              displayEmpty
            >
              <MenuItem value="">-- Sélectionnez --</MenuItem>
              <MenuItem value="oui">Oui</MenuItem>
              <MenuItem value="non">Non</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <Typography>{editableTalent?.poste === 'oui' ? 'Oui' : 'Non'}</Typography>
        )}
      </Box>

      {/* Badge visuel */}
      <Box mb={2}>
        <Typography variant="subtitle2" className="label-text">Statut professionnel</Typography>
        <Box
          display="inline-block"
          px={2}
          py={1}
          borderRadius="20px"
          bgcolor={editableTalent?.poste === 'oui' ? 'green' : 'grey.600'}
          color="white"
          fontSize="0.875rem"
          fontWeight={500}
        >
          {editableTalent?.poste === 'oui' ? '✅ Actuellement en poste' : '📌 Non en poste'}
        </Box>
      </Box>

      {/* Affichage conditionnel : poste actuel / entreprise / années */}
      {editableTalent?.poste === 'oui' && (
        <>
          <Box display="flex" gap={2} mb={2}>
            <Box flex={1}>
              <EditableField label="Poste actuel" field="posteActuelIntitule" isEditing={isEditing} value={editableTalent?.posteActuelIntitule} onChange={onFieldChange} />
            </Box>
            <Box flex={1}>
              <EditableField label="Entreprise actuelle" field="entrepriseActuelle" isEditing={isEditing} value={editableTalent?.entrepriseActuelle} onChange={onFieldChange} />
            </Box>
          </Box>

          <Box display="flex" gap={2} mb={2}>
            <Box flex={1}>
              <EditableField label="Années au poste" field="anneesPoste" isEditing={isEditing} value={editableTalent?.anneesPoste} onChange={onFieldChange} />
            </Box>
            <Box flex={1} />
          </Box>
        </>
      )}

      {/* Disponibilité */}
      <Box display="flex" gap={2} mb={2}>
        <Box flex={1}>
          <Typography variant="subtitle2" className="label-text">Disponibilité</Typography>
          {isEditing ? (
            <>
              <FormControl fullWidth>
                <InputLabel>Disponibilité</InputLabel>
                <Select
                  value={
                    ['immédiate', '1 mois', '3 mois'].includes(editableTalent?.disponibilite)
                      ? editableTalent.disponibilite
                      : 'autre'
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'autre') {
                      onFieldChange('disponibilite', '');
                    } else {
                      onFieldChange('disponibilite', val);
                    }
                  }}
                >
                  <MenuItem value="" disabled>-- Sélectionnez --</MenuItem>
                  {disponibiliteOptions.map((option, i) => (
                    <MenuItem key={i} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!disponibiliteOptions.some(opt => opt.value === editableTalent?.disponibilite) && (
                <TextField
                  type="date"
                  fullWidth
                  margin="normal"
                  value={editableTalent?.disponibilite || ''}
                  onChange={(e) => onFieldChange('disponibilite', e.target.value)}
                />
              )}
            </>
          ) : (
            <Typography>{displayOrUnknown(editableTalent?.disponibilite)}</Typography>
          )}
        </Box>
        <Box flex={1} />
      </Box>

      <Divider className="mission-divider" />
    </Paper>
  );
};

export default MissionInfo;
